import config from '../config';
import { isOAuthCallbackPath } from './oauthFlowState.js';

/**
 * Longest plain-text body still taken as the server's error message. The
 * backend's http.Error strings run to a couple of hundred characters; anything
 * longer is not one of them.
 */
const MAX_TEXT_ERROR_LENGTH = 500;

/**
 * What a failed response says went wrong, for the caller to show or match on.
 * A JSON body carries it as `message`. The Go handlers otherwise answer with
 * http.Error, whose plain-text body is the error string - written for the
 * user, and in places (stream source @handles, band social links) the only
 * thing that says what to do instead - so that is kept as well. An HTML body
 * (a proxy's or CloudFront's error page) or one too long to be such a string
 * falls back to the status text, as does an empty body.
 */
export const errorMessage = (data, statusText) => {
  if (data && typeof data === 'object') return data.message || statusText;
  if (typeof data === 'string') {
    const text = data.trim();
    if (text && !text.startsWith('<') && text.length <= MAX_TEXT_ERROR_LENGTH) return text;
  }
  return statusText;
};

/**
 * API paths whose 401 is the caller's to handle rather than the end of the
 * session. (A 403 never ends the session anywhere, since ENG-494; these
 * notes predate that and still explain why each path's 401 is exempt.) The
 * ENG-26 notification endpoints are requested on every signed-in page by
 * NotificationsProvider, against a backend (ENG-97) that may not have shipped them or may refuse accounts
 * that are not listeners; a refusal there is reported by the feed as
 * unauthorized, not turned into a logout from whatever page the user was on.
 * The ENG-379 locale preference is requested the same way, by
 * LocalePreferenceSync on every signed-in load, and reports the same.
 *
 * The ENG-375 country check is the same shape again: RegisterBand asks
 * `/me/eligibility` on mount, unprompted, against a backend that may
 * not have shipped the route (where a gateway in front of an unknown path
 * answers 401 as readily as 404) or may gate it by role. A refusal there is
 * the check failing, which that page already handles by leaving performer
 * sign-up open — it must not instead throw the musician out to /login, and a
 * 403 especially not, since the session it would end is still valid and the
 * path back is not even saved. `/me/musician-waitlist` is exempt with it: the
 * join is only reachable from that same page, and a refusal of it belongs in
 * the card as a failed join rather than as a logout mid-form.
 *
 * `/me/fx/loadout` (ENG-804) is asked unprompted by the watch page when a
 * live show starts its effects. A refusal there only means the viewer sees
 * the default vibe set and boost animation; it must not end the session of
 * someone watching a stream.
 *
 * `/me/notifications` covers the preferences endpoint too, which lives under
 * it (`/me/notifications/preferences`, ENG-409) rather than beside it. These
 * are substrings, not prefixes or path segments, so that entry also exempts
 * anything else starting `/me/notifications` — a sibling like
 * `/me/notifications-digest` would inherit the exemption without asking for
 * it. Give such a path its own entry (and its own test case below) only if it
 * wants one, and split this entry if one ever does not.
 */
const AUTO_LOGOUT_EXEMPT_PATHS = Object.freeze([
  '/me/notifications',
  '/me/locale-preference',
  '/me/eligibility',
  '/me/musician-waitlist',
  '/me/fx/loadout',
]);

/**
 * `GET /songs/{id}/lyrics` (ENG-414) is exempt by pattern rather than by
 * substring, because the song id sits in the middle of it. The watch page
 * asks for it unprompted on every song change for as long as a show is on
 * (ENG-415), which is the same hazard as the country check and worse for
 * being repeated: a JWT that expires during a two-hour set, a deployment
 * where ENG-414 has not shipped and a gateway answers 401/403 for the
 * unknown path, or a backend that gates lyrics by role would each throw a
 * listener out of the stream they are watching, mid-song, over a panel they
 * may not even have open. A refusal there is "no lyrics for you", which the
 * panel already says; it must not instead end the session.
 *
 * The OAuth code exchange is exempt as well (ENG-406). authHeader() attaches
 * whatever session the browser already holds to `/auth/{provider}/callback`,
 * but that endpoint's 401/403 is a verdict on the one-time code — a spent one
 * replayed out of history, an expired one, a linking request the backend
 * refused — and not on the session the request happened to carry. Ending that
 * session and hard-navigating to /login is precisely the eject ENG-406 is
 * about, and being a document navigation it would win the race against
 * anything OAuthPage decided to do with the rejection.
 *
 * `GET /me` itself (only that path, not every `/me/...`) is exempt too
 * (ENG-836): AccountFxPreferencesProvider asks it unprompted on every
 * signed-in page load for the Photosensitivity mode. A refusal there leaves
 * stream effects reduced and the switches disabled; it must not throw a
 * listener out of whatever page they were on.
 */
const AUTO_LOGOUT_EXEMPT_PATTERNS = Object.freeze([
  /\/auth\/[^/?#]+\/callback(?:[/?#]|$)/,
  /\/songs\/[^/?#]+\/lyrics(?:[/?#]|$)/,
  /\/me(?:[?#]|$)/,
]);

export const isAutoLogoutExempt = (url) =>
  AUTO_LOGOUT_EXEMPT_PATHS.some((path) => url.includes(path)) ||
  AUTO_LOGOUT_EXEMPT_PATTERNS.some((pattern) => pattern.test(url));

class FetchWrapper {
  constructor() {
    this.get = this.request('GET');
    this.post = this.request('POST');
    this.put = this.request('PUT');
    this.patch = this.request('PATCH');
    this.delete = this.request('DELETE');
  }

  request(method) {
    return (url, body, cache = false) => {
      const requestOptions = {
        method,
        headers: this.authHeader(url),
        cache: cache ? 'force-cache' : 'no-store',
      };

      if (body) {
        if (body instanceof FormData) {
          // Do not set Content-Type header for FormData
          delete requestOptions.headers['Content-Type'];
          requestOptions.body = body;
        } else {
          requestOptions.headers['Content-Type'] = 'application/json';
          requestOptions.body = JSON.stringify(body);
        }
      }

      return fetch(url, requestOptions).then(response => this.handleResponse(response, url));
    };
  }

  authHeader(url) {
    // return auth header with jwt if user is logged in and request is to the api url
    const token = sessionStorage.getItem('jwt');
    const isLoggedIn = !!token;
    const isApiUrl = url.startsWith(config.apiBaseUrl);
    if (isLoggedIn && isApiUrl) {
      return { Authorization: `Bearer ${token}` };
    } else {
      return {};
    }
  }

  handleResponse(response, url) {
    return response.text().then(text => {
      let data;
      
      try {
        data = text && JSON.parse(text);
      } catch (e) {
        data = text;
      }
      
      if (!response.ok) {
        // Only a 401 ends the session. A 403 means the session is fine and
        // the caller isn't allowed this one thing (bandAuth answers 403 for a
        // non-member): it is rejected like any other error, for the caller to
        // show as a permission message. Logging out on 403 used to throw
        // listeners and moderators out of pages they could open (ENG-494).
        if (response.status === 401 && sessionStorage.getItem('jwt')) {
          if (!isAutoLogoutExempt(url)) {
            sessionStorage.removeItem('jwt');
            sessionStorage.removeItem('ProfileImageURL');
            sessionStorage.removeItem('userId');
            sessionStorage.removeItem('theme');

            const currentPath = window.location.pathname + window.location.search + window.location.hash;
            // Compared on the pathname, not the whole path: the callback is
            // only ever reached as `/oauth?code=…`, so the old full-path
            // comparison never matched it and stored the callback URL as the
            // page to return to. The next login then navigated straight back
            // onto a spent code and ended on the login error page (ENG-406).
            const { pathname } = window.location;
            if (pathname !== '/login' && !isOAuthCallbackPath(pathname)) {
              sessionStorage.setItem('post-login-redirect', currentPath);
            }

            window.location.href = '/login';
          }
          // Exempt paths: the rejection below is the caller's to handle.
        }

        // Callers tell "the server said something" from "it only failed" by
        // comparing message with statusText, so the fallback is the status text.
        const error = {
          message: errorMessage(data, response.statusText),
          // The stable machine-readable code from the ENG-384 error envelope
          // (`{"error": "song_is_instrumental", "message": …}`), for callers
          // that phrase the failure themselves rather than showing the
          // server's English. Null for the plain-text http.Error bodies the
          // rest of the API still answers with, so a caller that branches on
          // it falls through to the status code the way it did before.
          code: data && typeof data === 'object' && typeof data.error === 'string' ? data.error : null,
          status: response.status,
          statusText: response.statusText,
          // The input a validation refusal points at (`{"error": "invalid_application",
          // "field": "artistName"}`, ENG-476), so a form can mark that field.
          // Only present when the backend named one.
          ...(data && typeof data === 'object' && typeof data.field === 'string' ? { field: data.field } : {}),
          // The envelope's data for the caller to word the failure with
          // (`params.retry_after_seconds`, ENG-795's `params.song_id`), and
          // the not-found's `closest` song for "Did you mean ...?" (ENG-796).
          // Only present when the backend sent them.
          ...(data && typeof data === 'object' && data.params && typeof data.params === 'object' ? { params: data.params } : {}),
          ...(data && typeof data === 'object' && 'closest' in data ? { closest: data.closest } : {}),
        };
        return Promise.reject(error);
      }

      return data;
    });
  }
}

// Create a singleton instance
export const fetchWrapper = new FetchWrapper();

// Custom hook for React components that expect useFetchWrapper
export const useFetchWrapper = () => fetchWrapper;

export default fetchWrapper;
